import groupBy from 'lodash/groupBy'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeesCollection from 'modules/employees/store/collections/employees'
import { PlanningVersionPayload } from 'modules/shift_management/store/internal/models/PlanningVersion'
import { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import { NO_WORK_AREA } from 'modules/shift_management/store/internal/models/Shift'
import ShiftPermissions from 'modules/shift_management/store/internal/models/ShiftPermissions'
import ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

import buildEmployeeParamsFromResources from './buildEmployeeParamsFromResources'
import { EmployeePermissions } from './types'

export interface WorkAreaPayload {
  id: number
  name: string
  location_id: number
  location_name: string
  archived: boolean
}

const buildWorkAreaParamsFromResources = (
  workAreas: WorkAreaPayload[] | undefined,
  employees: EmployeePermissions[],
  planningVersions: PlanningVersionPayload[] | undefined,
  shifts: ShiftPayload[] | undefined,
  companyHolidays: CompanyHoliday[],
  contractVersions: ContractVersions
): ShiftsRow[] => {
  if (!workAreas) {
    return []
  }

  const groupedShiftsByWorkArea = groupBy(shifts, (shift) => shift.work_area_id)
  const shiftsNoWorkArea = groupedShiftsByWorkArea['null']
  const groupedShiftsByLocation = groupBy(shiftsNoWorkArea, (shift) => {
    const employee = employeesCollection.find({ id: shift.employee_id })
    return shift.location_id || employee?.attributes.location_id
  })

  return workAreas.map((workArea) => {
    const workAreaId = workArea.id || NO_WORK_AREA
    const shiftsFromWorkArea = workArea.id
      ? groupedShiftsByWorkArea[workArea.id]
      : groupedShiftsByLocation[workArea.location_id]

    // Send all the employees for each row even if they don't have shifts to make the shifts addition easier
    const shiftsRowsFromEmployees = buildEmployeeParamsFromResources(
      employees,
      [],
      planningVersions,
      shiftsFromWorkArea,
      companyHolidays,
      [],
      contractVersions
    )

    const canCreateShifts = employees.some((employee) => {
      return employee.permissions.create
    })

    // We won't handle leaves in the work area view
    const permissions = {
      create: canCreateShifts,
      create_leave: false,
      approve_leave: false,
      delete_leave: false,
    }

    return new ShiftsRow({
      id: workArea.id,
      title: workArea.name,
      subtitle: workArea.location_name,
      key: `${workArea.location_id}-${workAreaId}`,
      permissions: ShiftPermissions.buildFromRequest(permissions),

      employees: shiftsRowsFromEmployees.flatMap((row) => row.employees),
      urlParams: workArea.id ? undefined : { id: workArea.location_id },
    })
  })
}

export default buildWorkAreaParamsFromResources
